import { Box, Paragraph, Spinner, Text, VisuallyHidden } from '@zillow/constellation-10';
import React, { useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { DESCRIPTION_MATRIX } from 'app/shared/pages/MarketTrendsResultsPage';
import { Bedrooms } from 'app/shared/types/Bedrooms';
import { PropertyTypes } from 'app/shared/types/PropertyTypes';
import numberUtils from 'app/shared/utils/numberUtils';
import { capitalizeFirstLetter } from 'app/shared/utils/stringUtils';
import DataCard from 'app/shared/components/DataCard';
import LeftAlignedYAxisTick from 'app/shared/components/LeftAlignedYAxisTick';
import ChartTooltip from 'app/shared/components/ChartTooltip';

import type { RentCount } from 'app/shared/types/RentCount';

const BLUE_500 = '#0D4599';

function decorate(rentCounts: Array<RentCount>): Array<{ price: string; count: number }> {
  return rentCounts.map((rentCount, i) => {
    let price = numberUtils.asCurrency(rentCount.price);
    const isLast = i === rentCounts.length - 1;
    if (isLast) {
      price += '+';
    }
    return { ...rentCount, price };
  });
}

interface RentHistogramProps {
  rentCounts: Array<RentCount>;
  minRent: number | null;
  maxRent: number | null;
  filterDescription: string;
}

const RentHistogram: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<RentHistogramProps>>> = (
  props,
) => {
  const { minRent, maxRent, filterDescription } = props;

  const rentCounts = useMemo(() => decorate(props.rentCounts), [props.rentCounts]);
  const [isClientLoaded, setIsClientLoaded] = useState(false);
  useEffect(() => {
    setIsClientLoaded(true);
  }, []);

  const unitName =
    filterDescription === DESCRIPTION_MATRIX[Bedrooms.ALL][PropertyTypes.ALL]
      ? 'Rental Properties'
      : capitalizeFirstLetter(filterDescription);

  const AccessibleTable = (
    <VisuallyHidden>
      <table>
        <caption>Rent frequencies</caption>
        <thead>
          <tr>
            <th scope="col">Rent price</th>
            <th scope="col">Number of rental properties</th>
          </tr>
        </thead>
        <tbody>
          {rentCounts.map((rentCount) => {
            return (
              <tr key={rentCount.price}>
                <td>{rentCount.price}</td>
                <td>{rentCount.count}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </VisuallyHidden>
  );

  return (
    <DataCard
      heading="Rental price range"
      copyText={
        <>
          The price range for {filterDescription} is{' '}
          <strong>{numberUtils.asCurrency(minRent || rentCounts[0].price)}</strong> to{' '}
          <strong>{numberUtils.asCurrency(maxRent || rentCounts[rentCounts.length - 1].price)}</strong>.
        </>
      }
    >
      {isClientLoaded ? (
        <>
          <Box css={{ marginTop: '400', marginBottom: '200' }} aria-hidden>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={rentCounts.map((rc) => {
                  return { name: rc.price, ...rc };
                })}
                margin={{ top: 8, left: 24, right: 24, bottom: 8 }}
              >
                <XAxis dataKey="name" axisLine={false} tickLine={false} style={{ fontSize: '14px' }} />
                <YAxis axisLine={false} tickLine={false} tick={LeftAlignedYAxisTick} style={{ fontSize: '14px' }} />
                <Tooltip
                  formatter={(formatterProps: any) => {
                    return (
                      <>
                        <Text textStyle="body-xs" css={{ marginRight: '200' }} style={{ color: formatterProps.color }}>
                          ▬
                        </Text>
                        <Text textStyle="body-xs" css={{ marginRight: '200' }}>
                          {formatterProps.name}
                        </Text>
                        <strong>{formatterProps.payload.name}</strong>
                      </>
                    );
                  }}
                  content={ChartTooltip}
                />
                <CartesianGrid vertical={false} stroke="#F1F1F4" />
                <Bar dataKey="count" name={unitName} fill={BLUE_500} barSize={6} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
          {/* recharts Legend component is too buggy to use https://github.com/recharts/recharts/issues/2704 */}
          {/* oxlint-disable-next-line constellation/no-raw-spacing-values -- `marginLeft: 80px` falls between `1800` (72px) and `2400` (96px) on the numeric spacing scale */}
          <Paragraph textStyle="body-sm" css={{ marginLeft: '80px' }} aria-hidden>
            <Text css={{ color: 'text.accent.blue.hero', marginRight: '200' }}>▬</Text>
            {unitName}
          </Paragraph>
        </>
      ) : (
        // oxlint-disable-next-line constellation/no-raw-spacing-values -- `height: 250px` is past the end of the numeric sizes scale, which stops at `5000` (200px)
        <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '1', height: '250px' }}>
          <Spinner size="sm" />
        </Box>
      )}
      {AccessibleTable}
    </DataCard>
  );
};

export default RentHistogram;
